// Custom event logging
import {datadogRum} from "@datadog/browser-rum";
import {datadogLogs} from "@datadog/browser-logs";

function isServer() {
    return typeof window === 'undefined'
}

export const logCustomEvent = (
    eventName: string,
    properties?: Record<string, any>,
    level: 'info' | 'warn' | 'error' | 'debug' = 'info'
) => {
    try {
        // Log to Datadog Logs
        //@ts-ignore
        datadogLogs.logger[level](eventName, properties)

        // Add custom action to RUM
        datadogRum.addAction(eventName, properties)

        // Add user context if available
        if (properties?.userId) {
            datadogRum.setUser({
                id: properties.userId,
                ...properties.userContext
            })
        }
    } catch (error) {
        console.error('Failed to log custom event:', error)
    }
}

// API endpoint logging
export const logApiCall = (
    endpoint: string,
    method: string,
    payload?: any,
    response?: any,
    duration?: number,
    error?: Error
) => {
    if (isServer()) return;
    try {
        const logData = {
            endpoint,
            method,
            payload: payload ? JSON.stringify(payload) : undefined,
            response: response ? JSON.stringify(response) : undefined,
            duration,
            timestamp: new Date().toISOString(),
            name: error?.name,
            error: error?.message,
            stack: error?.stack
        }

        if (error) {
            logCustomEvent('api_call_error', logData, 'error')
        } else {
            logCustomEvent('api_call_success', logData, 'info')
        }
    } catch (error) {
        console.error('Failed to log custom event:', error)
        logCustomEvent('log_error', {
            event: 'Failed to log custom event',
            trace: error,
        }, 'error')
    }

}

// Logic error logging
export const logLogicError = (
    errorType: string,
    errorMessage: string,
    context?: Record<string, any>,
    error?: Error
) => {
    const logData = {
        errorType,
        errorMessage,
        context,
        stack: error?.stack,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined
    }

    logCustomEvent('logic_error', logData, 'error')

    // Also add error to RUM
    if (error) {
        datadogRum.addError(error, logData)
    }
}

// Render error logging
export const logRenderError = (
    componentName: string,
    error: Error,
    errorInfo?: any
) => {
    const logData = {
        componentName,
        errorMessage: error.message,
        stack: error.stack,
        errorInfo,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined
    }

    logCustomEvent('render_error', logData, 'error')
    datadogRum.addError(error, logData)
}

// Performance logging
export const logPerformance = (
    metric: string,
    value: number,
    context?: Record<string, any>
) => {
    const logData = {
        metric,
        value,
        context,
        timestamp: new Date().toISOString()
    }

    logCustomEvent('performance_metric', logData, 'info')
}

// User action logging
export const logUserAction = (
    action: string,
    element?: string,
    context?: Record<string, any>
) => {
    const logData = {
        action,
        element,
        context,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined
    }

    logCustomEvent('user_action', logData, 'info')
}


type eventType = "payment_confirmation" | "payment_processing" | "payment_start" | "payment_cancelled"
// Business logic tracking
export const logBusinessEvent = (
    eventName: eventType,
    properties?: Record<string, any>
) => {
    logCustomEvent('business_event', {
        event: eventName,
        ...properties
    }, 'info')
}